"use client";

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { usePathname } from "next/navigation";
import { get__lti_error_url, is_lti_launch, get__window_search_params } from "@/auth/utils/lti/lti-client";
import {
	LTI_EMBEDDED_SESSION_KEY,
	clear_lti_auth_service_worker,
	get_lti_platform_data,
	get_lti_session_storage,
	install_lti_auth_service_worker,
	install_lti_auth_token_responder,
} from "@/auth/utils/lti/lti-postmessage-client";

interface LtiContextType {
	is_lti_mode: boolean;
	set_lti_mode: (value: boolean) => void;
}

const LtiContext = createContext<LtiContextType>({
	is_lti_mode: false,
	set_lti_mode: () => {},
});

const LTI_EMBEDDED_MODE_KEY = "studystash:lti-embedded-mode";
const EMBEDDED_LTI_REFRESH_INTERVAL_MS = 20 * 60 * 1000;

function is_in_iframe(): boolean {
	if (typeof window === "undefined") return false;
	try {
		return window.self !== window.top;
	} catch {
		// Cross-origin access can throw; treat as embedded.
		return true;
	}
}

function is_embedded_lti_session(): boolean {
	if (typeof window === "undefined") return false;

	const params = get__window_search_params();
	if (params?.get("lti_embedded") === "true") {
		try {
			window.sessionStorage.setItem(LTI_EMBEDDED_MODE_KEY, "true");
		} catch {
			// ignore
		}
		return true;
	}

	try {
		return window.sessionStorage.getItem(LTI_EMBEDDED_MODE_KEY) === "true";
	} catch {
		return false;
	}
}

function clear_embedded_lti_session_marker(): void {
	try {
		window.sessionStorage.removeItem(LTI_EMBEDDED_MODE_KEY);
	} catch {
		// ignore
	}
}

function check_lti_mode(): boolean {
	const params = get__window_search_params();
	const is_lti_from_params = params ? is_lti_launch(params) : false;
	const is_lti_embedded = params?.get("lti_embedded") === "true";
	const is_lti_from_iframe = is_in_iframe();
	return is_lti_from_params || is_lti_embedded || is_lti_from_iframe;
}

function should_skip_lti_session_monitor(pathname: string | null): boolean {
	return Boolean(pathname?.includes("/lti-error") || pathname?.includes("/lti-bootstrap"));
}

function use_lti_mode_detection(params: {
	pathname: string | null;
	is_lti_mode: boolean;
	set_lti_mode: (value: boolean) => void;
}) {
	const { pathname, is_lti_mode, set_lti_mode } = params;

	useEffect(() => {
		const is_lti = check_lti_mode();
		if (is_lti !== is_lti_mode) {
			console.log("[LTI] Mode changed", { from: is_lti_mode, to: is_lti, pathname });
			set_lti_mode(is_lti);
		}
	}, [pathname, is_lti_mode, set_lti_mode]);
}

function use_embedded_lti_bearer_session(params: {
	locale: string;
	pathname: string | null;
}) {
	const { locale, pathname } = params;
	const last_redirect_ref = useRef<string | null>(null);

	useEffect(() => {
		if (should_skip_lti_session_monitor(pathname)) return;
		if (!is_embedded_lti_session()) return;

		let cancelled = false;
		install_lti_auth_token_responder();

		const redirect_to_relaunch = async (message: string) => {
			clear_embedded_lti_session_marker();
			await clear_lti_auth_service_worker();
			const url = get__lti_error_url(locale, message);
			if (last_redirect_ref.current === url) return;
			last_redirect_ref.current = url;
			window.location.href = url;
		};

		const restore_service_worker_token = async () => {
			const access_token =
				await get_lti_platform_data(LTI_EMBEDDED_SESSION_KEY) ??
				get_lti_session_storage(LTI_EMBEDDED_SESSION_KEY);
			if (!access_token || cancelled) return false;
			return install_lti_auth_service_worker(access_token);
		};

		const try_refresh = async () => {
			try {
				await restore_service_worker_token();
				const refresh_res = await fetch("/api/lti/refresh", {
					method: "POST",
					cache: "no-store",
				});
				const refresh_data = refresh_res.ok ? await refresh_res.json() : null;
				if (cancelled) return;

				if (!refresh_res.ok || refresh_data?.ok === false) {
					console.log("[LTI Embedded] Bearer refresh failed", {
						status: refresh_res.status,
						error: refresh_data?.error,
					});
					await redirect_to_relaunch("Your session has expired. Please relaunch StudyStash from your LMS.");
					return;
				}

				if (typeof refresh_data?.access_token !== "string") {
					console.log("[LTI Embedded] Bearer refresh missing access token");
					await redirect_to_relaunch("Your session has expired. Please relaunch StudyStash from your LMS.");
					return;
				}

				const service_worker_ready = await install_lti_auth_service_worker(refresh_data.access_token);
				if (!service_worker_ready) {
					console.log("[LTI Embedded] Service worker token refresh failed");
					await redirect_to_relaunch("Your session has expired. Please relaunch StudyStash from your LMS.");
					return;
				}

				console.log("[LTI Embedded] Bearer session refreshed");
			} catch {
				// Network hiccup; retry on the next interval or tab visibility change.
			}
		};

		const on_tab_switch = () => {
			if (document.visibilityState === "visible") {
				try_refresh();
			}
		};

		const interval = window.setInterval(() => {
			try_refresh();
		}, EMBEDDED_LTI_REFRESH_INTERVAL_MS);

		console.log("[LTI Embedded] Bearer refresh monitor started", {
			interval_seconds: Math.round(EMBEDDED_LTI_REFRESH_INTERVAL_MS / 1000),
		});
		restore_service_worker_token();
		document.addEventListener("visibilitychange", on_tab_switch);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
			document.removeEventListener("visibilitychange", on_tab_switch);
		};
	}, [locale, pathname]);
}

export function LtiProvider({ children, locale }: { children: ReactNode; locale: string }) {
	const pathname = usePathname();
	const [is_lti_mode, setIsLtiMode] = useState(check_lti_mode);

	use_lti_mode_detection({
		pathname,
		is_lti_mode,
		set_lti_mode: setIsLtiMode,
	});
	use_embedded_lti_bearer_session({
		locale,
		pathname,
	});

	return (
		<LtiContext.Provider value={{ is_lti_mode, set_lti_mode: setIsLtiMode }}>
			{children}
		</LtiContext.Provider>
	);
}

export function use_lti_mode(): boolean {
	const { is_lti_mode } = useContext(LtiContext);
	return is_lti_mode;
}

export function use_set_lti_mode(): (value: boolean) => void {
	const { set_lti_mode } = useContext(LtiContext);
	return set_lti_mode;
}
